/**
 * Storyblok Editable Attributes Utility
 *
 * Extracts and formats editable attributes from Storyblok blocks
 * for use in the Visual Editor.
 */

/**
 * Parse the _editable property from a Storyblok block
 * @param {string|Object} editable - The _editable property from Storyblok block
 * @returns {Object|null} Parsed editable data with name, space, uid, and id, or null
 */
function parseEditable(editable) {
  if (!editable) return null;

  try {
    // If it's already an object, return it
    if (typeof editable === 'object') {
      return editable;
    }

    // If it's a string, it might be HTML comment format: <!--#storyblok#{"name":"...","space":"...","uid":"...","id":"..."}-->
    if (typeof editable === 'string') {
      // Extract JSON from HTML comment
      const match = editable.match(/\{.*\}/);
      if (match) {
        return JSON.parse(match[0]);
      }
      // If it's already JSON string, parse it directly
      try {
        return JSON.parse(editable);
      } catch (e) {
        // Not JSON, return null
        return null;
      }
    }

    return null;
  } catch (error) {
    console.warn('[Storyblok] Error parsing _editable:', error);
    return null;
  }
}

/**
 * Get editable attributes for a Storyblok block
 * @param {Object} block - Storyblok block with _editable property
 * @returns {Object} Object with data-blok-c and data-blok-uid attributes
 */
export function getStoryblokEditableAttributes(block) {
  if (!block || !block._editable) {
    return {};
  }

  const editableData = parseEditable(block._editable);

  if (!editableData) {
    return {};
  }

  const { name, space, uid, id } = editableData;

  // data-blok-c should contain the stringified JSON
  const dataBlokC = JSON.stringify({ name, space, uid, id });

  // data-blok-uid should be in the pattern: ${id}-${uid}
  const dataBlokUid = `${id}-${uid}`;

  return {
    'data-blok-c': dataBlokC,
    'data-blok-uid': dataBlokUid,
  };
}

/**
 * Check if a block has editable attributes
 * @param {Object} block - Storyblok block
 * @returns {boolean} True if block has _editable property
 */
export function hasEditableAttributes(block) {
  return !!(block && block._editable);
}
